import { CallState, LoadingState } from '@lcs/shared/api';
import { Action, createReducer, on } from '@ngrx/store';
import { onReset } from 'src/app/authentication/reset-reducer';
import {
   promptCallDialBegin,
   promptCallHungUp,
   promptCallPickedUp,
   recordingPrompt,
   reviewingPrompt,
   savingPrompt,
} from 'src/app/signalr/recording-new-prompt-hub/recording-new-prompt-hub.actions';
import { VoiceMailGreetingPromptOption } from '../../mailbox-details/models/voicemail-greeting-prompt-option.model';
import { RecordedAudioOutput } from '../models/recorded-prompt';
import * as AddPromptActions from './add-prompt.actions';

export const addPromptFeatureKey = 'addPrompt';
export interface AddPromptState {
   callState: CallState;
   recordingNewFromExtension: boolean;
   recordingNewFromAdd: boolean;
   recordingFileForUpdatePrompt: boolean;
   voiceMailGreetingPrompt: VoiceMailGreetingPromptOption | null;
   recordedFile: RecordedAudioOutput | null;
   recordingPromptCallDetailId: number | null;
   recordingPromptCallChannelId: string | null;
   recordingPromptCallServerIp: string | null;
   recordingPromptCallStatus: string | null;
   promptRecordingCallSessionId: string | null;
   isContinuous: boolean;
}

export const initialState: AddPromptState = {
   callState: LoadingState.Init,
   promptRecordingCallSessionId: null,
   recordedFile: null,
   recordingNewFromAdd: false,
   recordingNewFromExtension: false,
   recordingFileForUpdatePrompt: false,
   recordingPromptCallDetailId: null,
   recordingPromptCallChannelId: null,
   recordingPromptCallServerIp: null,
   recordingPromptCallStatus: null,
   voiceMailGreetingPrompt: null,
   isContinuous: false,
};

const addPromptReducer = createReducer(
   initialState,
   onReset(initialState),
   on(AddPromptActions.createPrompt, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         isContinuous: false,
      };
   }),
   on(AddPromptActions.createPromptAndAddAnother, AddPromptActions.createNewFromExtensionPrompt, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         isContinuous: true,
      };
   }),
   on(AddPromptActions.createPromptSuccess, AddPromptActions.createPromptAndAddAnotherSuccess, (state) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         recordingPromptCallStatus: null,
      };
   }),
   on(AddPromptActions.createPromptFailure, (state) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         recordingPromptCallStatus: null,
      };
   }),
   on(AddPromptActions.recordPrompt, (state, data) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         recordingPromptCallStatus: `Calling ${data.routeToExtension ? data.routeToExtension : data.destinationNumber}`,
         recordingPromptCallDetailId: null,
         recordingPromptCallChannelId: null,
         recordingPromptCallServerIp: null,
         isContinuous: data.isContinuous,
      };
   }),
   on(
      AddPromptActions.recordPromptRequestSend,
      (state, { promptRecordingCallDetailId, promptRecordingCallSessionId }) => {
         return {
            ...state,
            callState: LoadingState.Loading,
            recordingPromptCallDetailId: promptRecordingCallDetailId,
            promptRecordingCallSessionId: promptRecordingCallSessionId,
         };
      }
   ),
   on(AddPromptActions.recordPromptFileOnly, (state, data) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         recordingPromptCallStatus: `Calling ${data.routeToExtension ? data.routeToExtension : data.destinationNumber}`,
         recordingFileForUpdatePrompt: true,
      };
   }),
   on(
      AddPromptActions.setRecordingFromExtensionVariables,
      (state, { creatingNewFromExtension, recordingNewFromAdd, voiceMailGreetingPrompt }) => {
         return {
            ...state,
            recordingNewFromAdd: recordingNewFromAdd,
            recordingNewFromExtension: creatingNewFromExtension,
            voiceMailGreetingPrompt: voiceMailGreetingPrompt,
         };
      }
   ),
   on(AddPromptActions.stopMicrophoneRecording, (state, { file }) => {
      return {
         ...state,
         recordedFile: file,
      };
   }),
   on(AddPromptActions.clearMicrophoneRecording, (state) => {
      return {
         ...state,
         recordedFile: null,
      };
   }),
   on(promptCallDialBegin, (state, { channelId, serverIp }) => {
      return {
         ...state,
         recordingPromptCallChannelId: channelId,
         recordingPromptCallServerIp: serverIp,
         recordingPromptCallStatus: 'Dialing',
      };
   }),
   on(promptCallPickedUp, (state, { channelId, serverIp }) => {
      return {
         ...state,
         recordingPromptCallChannelId: channelId,
         recordingPromptCallServerIp: serverIp,
         recordingPromptCallStatus: 'In Call',
      };
   }),
   on(promptCallHungUp, (state) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         recordingPromptCallChannelId: null,
         recordingPromptCallServerIp: null,
         recordingPromptCallStatus: null,
         recordingPromptCallDetailId: null,
      };
   }),
   on(recordingPrompt, (state) => {
      return {
         ...state,
         recordingPromptCallStatus: 'Recording Prompt',
      };
   }),
   on(reviewingPrompt, (state) => {
      return {
         ...state,
         recordingPromptCallStatus: 'Reviewing Prompt',
      };
   }),
   on(savingPrompt, (state) => {
      return {
         ...state,
         recordingPromptCallStatus: 'Saving Prompt',
      };
   })
);

export function reducer(state: AddPromptState | undefined, action: Action) {
   return addPromptReducer(state, action);
}
